import { type LucideIcon } from 'lucide-react';

export type TableRowAction<T> = {
  icon: string;
  /** Optional Lucide override; when present the table renders the SVG icon
   * instead of the icon-font glyph for `icon`. `icon` is still the action id. */
  lucide?: LucideIcon;
  label: string;
  // Receives the selected rows (or the single clicked row when nothing is
  // selected). `isVisible` stays single-item — it's evaluated per rendered row
  // to decide whether that row shows the action icon.
  onClick?: (items: T[]) => void;
  isVisible?: (item: T) => boolean;
};

export function useTableRowActions<T>(items: T[], actions: TableRowAction<T>[]) {
  const actionProps = actions.map((a) => ({
    icon: a.icon,
    lucide: a.lucide,
    label: a.label,
  }));

  const onActionClick = (rowIndices: number[], icon: string) => {
    const selectedItems = rowIndices.map((i) => items[i]).filter((x): x is T => x != null);
    if (!selectedItems.length) return;
    actions.find((a) => a.icon === icon)?.onClick?.(selectedItems);
  };

  const isActionVisible = (rowIndex: number, icon: string) => {
    const action = actions.find((a) => a.icon === icon);
    if (!action?.isVisible) return true;
    const item = items[rowIndex];
    return item ? action.isVisible(item) : true;
  };

  return {
    actionProps,
    onActionClick,
    isActionVisible,
  };
}
